import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { NotFoundErrorCode, OfferErrorCode, OfferStatus, ValidationErrorCode } from '@today/types'
import { isAxiosError } from 'axios'
import { useEffect, useMemo, useReducer } from 'react'
import { type OfferDTO, offerService } from '@/services/offer.service'
import { type CatalogueRow, orderCatalogue } from './catalogue-order'
import { httpProductPort, type ProductPort } from './product-port'
import { canSave, selectionReducer } from './selection'

interface ApiErrorBody {
	error?: { code?: string }
}

const NOT_EDITABLE = 'This offer has already been sent and can no longer be edited.'

/** The message a refused save shows (TO-123 contract table). */
export const saveErrorOf = (error: unknown): string => {
	const code = isAxiosError<ApiErrorBody>(error) ? error.response?.data?.error?.code : undefined
	switch (code) {
		case OfferErrorCode.OFFER_NOT_EDITABLE:
		case OfferErrorCode.OFFER_STATUS_CONFLICT:
			return NOT_EDITABLE
		case NotFoundErrorCode.PRODUCT_NOT_FOUND:
			return 'One of the selected products no longer exists. Remove it and save again.'
		case ValidationErrorCode.TEXT_NOT_AVAILABLE:
			return "A required legal text is missing for this offer's language."
		case NotFoundErrorCode.OFFER_NOT_FOUND:
			return 'This offer could not be found.'
		default:
			return 'The selection could not be saved. Try again.'
	}
}

/**
 * Offer Wizard Step 2 (TO-123): the deterministic catalogue (via the
 * consumer-owned {@link ProductPort}), the offer's saved product order, and the
 * manager's unsaved edit of it with add / move / remove / save.
 */
export function useOfferProductSelection(offerId: string, port: ProductPort = httpProductPort) {
	const client = useQueryClient()
	const offerKey = ['offer', offerId] as const

	const offer = useQuery<OfferDTO>({ queryKey: offerKey, queryFn: () => offerService.get(offerId) })
	const catalogue = useQuery<CatalogueRow[]>({
		queryKey: ['offer-wizard', 'catalogue'],
		queryFn: async () => {
			const [products, categories] = await Promise.all([port.listActive(), port.listCategories()])
			return orderCatalogue(products, categories)
		},
	})

	const [state, dispatch] = useReducer(selectionReducer, { saved: [], draft: [] })
	const storedIds = offer.data?.productIds
	useEffect(() => {
		if (storedIds) dispatch({ type: 'saved', productIds: storedIds })
	}, [storedIds])

	const save = useMutation({
		mutationFn: (productIds: string[]) => offerService.replaceProducts(offerId, productIds),
		onSuccess: (saved) => {
			client.setQueryData(offerKey, saved)
			dispatch({ type: 'saved', productIds: saved.productIds })
		},
	})

	const editable = offer.data?.status === OfferStatus.DRAFT
	const rows = catalogue.data ?? []
	const nameOf = useMemo(() => new Map(rows.map((r) => [r.productId, r.name])), [rows])

	const edit = (action: Parameters<typeof dispatch>[0]) => {
		save.reset()
		dispatch(action)
	}

	return {
		loading: offer.isPending || catalogue.isPending,
		loadFailed: offer.isError || catalogue.isError,
		editable,
		readOnlyMessage: offer.data && !editable ? NOT_EDITABLE : null,
		catalogue: rows,
		/** The unsaved ordered selection, with display names (unknown ids — e.g. retired products — keep their id). */
		selected: state.draft.map((productId) => ({ productId, name: nameOf.get(productId) ?? productId })),
		add: (productId: string) => edit({ type: 'add', productId }),
		moveUp: (productId: string) => edit({ type: 'moveUp', productId }),
		moveDown: (productId: string) => edit({ type: 'moveDown', productId }),
		remove: (productId: string) => edit({ type: 'remove', productId }),
		canSave: editable && canSave(state) && !save.isPending,
		save: () => save.mutate(state.draft),
		saving: save.isPending,
		saveError: save.isError ? saveErrorOf(save.error) : null,
	}
}
